import React, {useCallback, useEffect, useState} from 'react';
import {adminAPI} from '../services/api';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Edit2,
    Image,
    Layers,
    Plus,
    RefreshCw,
    Save,
    Search,
    Trash2,
    Upload,
    X
} from 'lucide-react';
import {toast} from 'react-toastify';

// ==================== Pagination Component ====================
const Pagination = ({pagination, onPageChange}) => {
    if (!pagination || pagination.totalPages <= 1) return null;
    const {page, totalPages, total} = pagination;

    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-700">{total} results</span>
            <div className="flex items-center space-x-1">
                <button onClick={() => onPageChange(1)} disabled={page === 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsLeft className="w-4 h-4"/>
                </button>
                <button onClick={() => onPageChange(page - 1)} disabled={page === 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronLeft className="w-4 h-4"/>
                </button>
                <span className="text-sm px-2">{page} / {totalPages}</span>
                <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronRight className="w-4 h-4"/>
                </button>
                <button onClick={() => onPageChange(totalPages)} disabled={page === totalPages}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsRight className="w-4 h-4"/>
                </button>
            </div>
        </div>
    );
};

// ==================== Image Upload Widget ====================
const ImageUploadWidget = ({currentUrl, onUpload, imageType, label}) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            await onUpload(file, imageType);
            toast.success(`${label} uploaded`);
        } catch (error) {
            toast.error('Upload failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="border rounded-lg p-3">
            <label className="block text-xs font-medium text-gray-500 mb-2">{label}</label>
            {currentUrl && (
                <img src={currentUrl} alt={label} className="h-16 object-contain mb-2 rounded"/>
            )}
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                   onChange={handleFileChange} className="hidden"/>
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
                <Upload className="w-3 h-3 mr-1"/>
                {uploading ? 'Uploading...' : currentUrl ? 'Replace' : 'Upload'}
            </button>
        </div>
    );
};

// ==================== Modal ====================
const Modal = ({title, onClose, children}) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
             onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">{title}</h3>
                <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

// ==================== Dynamic Form ====================
const DynamicForm = ({fields, values, onChange, idField, isNew}) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[isNew && idField, ...fields].filter(Boolean).map(field => (
            <div key={field} className={field === idField && !isNew ? 'hidden' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{field}</label>
                {(values[field] || '').length > 100 ? (
                    <textarea value={values[field] || ''} onChange={e => onChange(field, e.target.value)}
                              className="w-full border rounded-md px-3 py-2 text-sm" rows={3}
                              disabled={field === idField && !isNew}/>
                ) : (
                    <input type="text" value={values[field] || ''} onChange={e => onChange(field, e.target.value)}
                           className="w-full border rounded-md px-3 py-2 text-sm"
                           disabled={field === idField && !isNew}/>
                )}
            </div>
        ))}
    </div>
);

// ==================== Main Component ====================
const AdminTcgData = () => {
    const [tcgConfigs, setTcgConfigs] = useState([]);
    const [selectedTcg, setSelectedTcg] = useState(null);
    const [selectedSet, setSelectedSet] = useState(null);
    const [schema, setSchema] = useState(null);

    // Sets state
    const [sets, setSets] = useState([]);
    const [setsPagination, setSetsPagination] = useState(null);
    const [setsSearch, setSetsSearch] = useState('');
    const [setsPage, setSetsPage] = useState(1);
    const [loadingSets, setLoadingSets] = useState(false);

    // Cards state
    const [cards, setCards] = useState([]);
    const [cardsPagination, setCardsPagination] = useState(null);
    const [cardsSearch, setCardsSearch] = useState('');
    const [cardsPage, setCardsPage] = useState(1);
    const [loadingCards, setLoadingCards] = useState(false);

    // Modal state
    const [modal, setModal] = useState(null); // { type: 'editSet'|'editCard'|'createSet'|'createCard', data: {} }
    const [formValues, setFormValues] = useState({});
    const [saving, setSaving] = useState(false);

    const [loading, setLoading] = useState(true);

    // Fetch TCG configs
    useEffect(() => {
        const fetchConfigs = async () => {
            try {
                const response = await adminAPI.getTcgConfigs();
                setTcgConfigs(response.data.data.tcgConfigs || []);
            } catch (error) {
                toast.error('Failed to load TCG configurations');
            } finally {
                setLoading(false);
            }
        };
        fetchConfigs();
    }, []);

    // Fetch schema when TCG selected
    useEffect(() => {
        if (!selectedTcg) {
            setSchema(null);
            return;
        }
        const fetchSchema = async () => {
            try {
                const response = await adminAPI.getTcgSchema(selectedTcg.name);
                setSchema(response.data.data);
            } catch (error) {
                toast.error('Failed to load schema for ' + selectedTcg.name);
            }
        };
        fetchSchema();
    }, [selectedTcg]);

    // Fetch sets
    const fetchSets = useCallback(async () => {
        if (!selectedTcg) return;
        setLoadingSets(true);
        try {
            const response = await adminAPI.getTcgSets(selectedTcg.name, {page: setsPage, search: setsSearch});
            setSets(response.data.data.sets);
            setSetsPagination(response.data.data.pagination);
        } catch (error) {
            toast.error('Failed to load sets');
        } finally {
            setLoadingSets(false);
        }
    }, [selectedTcg, setsPage, setsSearch]);

    useEffect(() => {
        if (selectedTcg) fetchSets();
    }, [fetchSets, selectedTcg]);

    // Fetch cards
    const fetchCards = useCallback(async () => {
        if (!selectedTcg || !selectedSet) return;
        setLoadingCards(true);
        try {
            const setId = selectedSet[schema?.sets?.idColumn || 'id'];
            const response = await adminAPI.getTcgCards(selectedTcg.name, setId, {
                page: cardsPage,
                search: cardsSearch
            });
            setCards(response.data.data.cards);
            setCardsPagination(response.data.data.pagination);
        } catch (error) {
            toast.error('Failed to load cards');
        } finally {
            setLoadingCards(false);
        }
    }, [selectedTcg, selectedSet, cardsPage, cardsSearch, schema]);

    useEffect(() => {
        if (selectedSet) fetchCards();
    }, [fetchCards, selectedSet]);

    // TCG selection
    const handleSelectTcg = (tcg) => {
        setSelectedTcg(tcg);
        setSelectedSet(null);
        setSets([]);
        setCards([]);
        setSetsSearch('');
        setCardsSearch('');
        setSetsPage(1);
        setCardsPage(1);
    };

    // Set selection -> drill into cards
    const handleSelectSet = (set) => {
        setSelectedSet(set);
        setCards([]);
        setCardsSearch('');
        setCardsPage(1);
    };

    // ==================== CRUD Handlers ====================
    const openCreateSet = () => {
        setFormValues({});
        setModal({type: 'createSet'});
    };

    const openEditSet = (set) => {
        setFormValues({...set});
        setModal({type: 'editSet', data: set});
    };

    const openCreateCard = () => {
        setFormValues({set_id: selectedSet[schema?.sets?.idColumn || 'id']});
        setModal({type: 'createCard'});
    };

    const openEditCard = async (card) => {
        // Fetch full card data
        try {
            const cardId = card[schema?.cards?.idColumn || 'id'];
            const response = await adminAPI.getTcgCard(selectedTcg.name, cardId);
            setFormValues({...response.data.data});
            setModal({type: 'editCard', data: response.data.data});
        } catch {
            setFormValues({...card});
            setModal({type: 'editCard', data: card});
        }
    };

    const handleFormChange = (field, value) => {
        setFormValues(prev => ({...prev, [field]: value}));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (modal.type === 'createSet') {
                await adminAPI.createTcgSet(selectedTcg.name, formValues);
                toast.success('Set created');
                fetchSets();
            } else if (modal.type === 'editSet') {
                const setId = modal.data[schema.sets.idColumn];
                await adminAPI.updateTcgSet(selectedTcg.name, setId, formValues);
                toast.success('Set updated');
                fetchSets();
            } else if (modal.type === 'createCard') {
                await adminAPI.createTcgCard(selectedTcg.name, formValues);
                toast.success('Card created');
                fetchCards();
            } else if (modal.type === 'editCard') {
                const cardId = modal.data[schema.cards.idColumn];
                await adminAPI.updateTcgCard(selectedTcg.name, cardId, formValues);
                toast.success('Card updated');
                fetchCards();
            }
            setModal(null);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSet = async (set) => {
        const setId = set[schema.sets.idColumn];
        if (!window.confirm(`Delete set "${set.name || setId}" and all its cards?`)) return;
        try {
            await adminAPI.deleteTcgSet(selectedTcg.name, setId);
            toast.success('Set deleted');
            if (selectedSet && selectedSet[schema.sets.idColumn] === setId) setSelectedSet(null);
            fetchSets();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Delete failed');
        }
    };

    const handleDeleteCard = async (card) => {
        const cardId = card[schema.cards.idColumn];
        if (!window.confirm(`Delete card "${card.name || cardId}"?`)) return;
        try {
            await adminAPI.deleteTcgCard(selectedTcg.name, cardId);
            toast.success('Card deleted');
            fetchCards();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Delete failed');
        }
    };

    // Image upload handlers
    const handleSetImageUpload = async (file, imageType) => {
        const setId = modal.data[schema.sets.idColumn];
        const formData = new FormData();
        formData.append('image', file);
        formData.append('imageType', imageType);
        await adminAPI.uploadSetImage(selectedTcg.name, setId, formData);
        fetchSets();
    };

    const handleCardImageUpload = async (file, imageType) => {
        const cardId = modal.data[schema.cards.idColumn];
        const formData = new FormData();
        formData.append('image', file);
        formData.append('imageType', imageType);
        await adminAPI.uploadCardImage(selectedTcg.name, cardId, formData);
        fetchCards();
    };

    // Search debounce
    const handleSetsSearchChange = (value) => {
        setSetsSearch(value);
        setSetsPage(1);
    };

    const handleCardsSearchChange = (value) => {
        setCardsSearch(value);
        setCardsPage(1);
    };

    // ==================== Render ====================
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400"/>
            </div>
        );
    }

    // Get first image column value for thumbnails
    const getSetThumb = (set) => {
        if (!schema) return null;
        const cols = schema.sets.imageTypes || [];
        for (const t of cols) {
            const colName = Object.entries(getSchema_frontend(selectedTcg.name)?.sets?.imageColumns || {}).find(([k]) => k === t)?.[1];
            if (colName && set[colName]) return set[colName];
        }
        // Fallback: check common column names
        for (const col of ['logo_image_url', 'symbol_image_url', 'icon_svg_uri']) {
            if (set[col]) return set[col];
        }
        return null;
    };

    const getCardThumb = (card) => {
        for (const col of ['small_image_url', 'image_url', 'image_cdn_url']) {
            if (card[col]) return card[col];
        }
        return null;
    };

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                    <button onClick={() => {
                        setSelectedTcg(null);
                        setSelectedSet(null);
                    }}
                            className="hover:text-gray-700">TCG Data
                    </button>
                    {selectedTcg && (
                        <>
                            <ChevronRight className="w-4 h-4"/>
                            <button onClick={() => setSelectedSet(null)} className="hover:text-gray-700">
                                {selectedTcg.displayName || selectedTcg.name}
                            </button>
                        </>
                    )}
                    {selectedSet && (
                        <>
                            <ChevronRight className="w-4 h-4"/>
                            <span
                                className="text-gray-900">{selectedSet.name || selectedSet[schema?.sets?.idColumn]}</span>
                        </>
                    )}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Layers className="w-6 h-6 mr-2"/>
                    TCG Data Management
                </h1>
            </div>

            {/* Level 1: TCG Selector */}
            {!selectedTcg && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tcgConfigs.filter(t => t.isActive).map(tcg => (
                        <button key={tcg.id} onClick={() => handleSelectTcg(tcg)}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:border-blue-300 hover:shadow-md transition-all">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-gray-900">{tcg.displayName || tcg.name}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{tcg.description || tcg.name}</p>
                                    <p className="text-xs text-gray-400 mt-1">DB: {tcg.databaseName}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400"/>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Level 2: Sets List */}
            {selectedTcg && !selectedSet && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center space-x-2">
                            <button onClick={() => setSelectedTcg(null)} className="p-1 hover:bg-gray-100 rounded">
                                <ArrowLeft className="w-5 h-5"/>
                            </button>
                            <h2 className="font-semibold">Sets — {selectedTcg.displayName || selectedTcg.name}</h2>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400"/>
                                <input type="text" placeholder="Search sets..." value={setsSearch}
                                       onChange={e => handleSetsSearchChange(e.target.value)}
                                       className="pl-8 pr-3 py-2 border rounded-md text-sm w-48"/>
                            </div>
                            <button onClick={fetchSets} className="p-2 hover:bg-gray-100 rounded" title="Refresh">
                                <RefreshCw className={`w-4 h-4 ${loadingSets ? 'animate-spin' : ''}`}/>
                            </button>
                            <button onClick={openCreateSet}
                                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
                                <Plus className="w-4 h-4 mr-1"/> Add Set
                            </button>
                        </div>
                    </div>

                    {loadingSets ? (
                        <div className="flex justify-center py-8">
                            <RefreshCw className="w-5 h-5 animate-spin text-gray-400"/>
                        </div>
                    ) : sets.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No sets found</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead
                                    className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3">Image</th>
                                    <th className="px-4 py-3">ID</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Cards</th>
                                    <th className="px-4 py-3">Release Date</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {sets.map(set => {
                                    const setId = set[schema?.sets?.idColumn || 'id'];
                                    const thumb = getSetThumb(set);
                                    return (
                                        <tr key={setId} className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => handleSelectSet(set)}>
                                            <td className="px-4 py-2">
                                                {thumb ? <img src={thumb} alt="" className="h-8 w-8 object-contain"/> :
                                                    <Image className="w-8 h-8 text-gray-300"/>}
                                            </td>
                                            <td className="px-4 py-2 text-sm font-mono text-gray-600">{setId}</td>
                                            <td className="px-4 py-2 text-sm font-medium">{set.name}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{set.card_count || 0}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{set.release_date || set.released_at || '—'}</td>
                                            <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center space-x-1">
                                                    <button onClick={() => openEditSet(set)}
                                                            className="p-1 hover:bg-blue-50 rounded text-blue-600"
                                                            title="Edit">
                                                        <Edit2 className="w-4 h-4"/>
                                                    </button>
                                                    <button onClick={() => handleDeleteSet(set)}
                                                            className="p-1 hover:bg-red-50 rounded text-red-600"
                                                            title="Delete">
                                                        <Trash2 className="w-4 h-4"/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <Pagination pagination={setsPagination} onPageChange={setSetsPage}/>
                </div>
            )}

            {/* Level 3: Cards List */}
            {selectedTcg && selectedSet && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center space-x-2">
                            <button onClick={() => setSelectedSet(null)} className="p-1 hover:bg-gray-100 rounded">
                                <ArrowLeft className="w-5 h-5"/>
                            </button>
                            <h2 className="font-semibold">Cards
                                — {selectedSet.name || selectedSet[schema?.sets?.idColumn]}</h2>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400"/>
                                <input type="text" placeholder="Search cards..." value={cardsSearch}
                                       onChange={e => handleCardsSearchChange(e.target.value)}
                                       className="pl-8 pr-3 py-2 border rounded-md text-sm w-48"/>
                            </div>
                            <button onClick={fetchCards} className="p-2 hover:bg-gray-100 rounded" title="Refresh">
                                <RefreshCw className={`w-4 h-4 ${loadingCards ? 'animate-spin' : ''}`}/>
                            </button>
                            <button onClick={openCreateCard}
                                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
                                <Plus className="w-4 h-4 mr-1"/> Add Card
                            </button>
                        </div>
                    </div>

                    {loadingCards ? (
                        <div className="flex justify-center py-8">
                            <RefreshCw className="w-5 h-5 animate-spin text-gray-400"/>
                        </div>
                    ) : cards.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No cards found</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead
                                    className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3">Image</th>
                                    <th className="px-4 py-3">ID</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Number</th>
                                    <th className="px-4 py-3">Rarity</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                {cards.map(card => {
                                    const cardId = card[schema?.cards?.idColumn || 'id'];
                                    const thumb = getCardThumb(card);
                                    return (
                                        <tr key={cardId} className="hover:bg-gray-50">
                                            <td className="px-4 py-2">
                                                {thumb ? <img src={thumb} alt=""
                                                              className="h-12 w-9 object-contain rounded"/> :
                                                    <Image className="w-9 h-12 text-gray-300"/>}
                                            </td>
                                            <td className="px-4 py-2 text-sm font-mono text-gray-600">{cardId}</td>
                                            <td className="px-4 py-2 text-sm font-medium">{card.name}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{card.number || card.card_num || card.collector_number || '—'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{card.rarity || '—'}</td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center space-x-1">
                                                    <button onClick={() => openEditCard(card)}
                                                            className="p-1 hover:bg-blue-50 rounded text-blue-600"
                                                            title="Edit">
                                                        <Edit2 className="w-4 h-4"/>
                                                    </button>
                                                    <button onClick={() => handleDeleteCard(card)}
                                                            className="p-1 hover:bg-red-50 rounded text-red-600"
                                                            title="Delete">
                                                        <Trash2 className="w-4 h-4"/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <Pagination pagination={cardsPagination} onPageChange={setCardsPage}/>
                </div>
            )}

            {/* Edit/Create Modal */}
            {modal && schema && (
                <Modal
                    title={
                        modal.type === 'createSet' ? 'Create Set' :
                            modal.type === 'editSet' ? 'Edit Set' :
                                modal.type === 'createCard' ? 'Create Card' : 'Edit Card'
                    }
                    onClose={() => setModal(null)}
                >
                    {(modal.type === 'createSet' || modal.type === 'editSet') && (
                        <>
                            <DynamicForm
                                fields={schema.sets.editableColumns}
                                values={formValues}
                                onChange={handleFormChange}
                                idField={schema.sets.idColumn}
                                isNew={modal.type === 'createSet'}
                            />
                            {modal.type === 'editSet' && schema.sets.imageTypes.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Images</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {schema.sets.imageTypes.map(type => (
                                            <ImageUploadWidget key={type} imageType={type} label={type}
                                                               currentUrl={formValues[Object.values(TCG_IMAGE_COLS[selectedTcg.name]?.sets || {})[schema.sets.imageTypes.indexOf(type)]] || ''}
                                                               onUpload={handleSetImageUpload}/>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {(modal.type === 'createCard' || modal.type === 'editCard') && (
                        <>
                            <DynamicForm
                                fields={schema.cards.editableColumns}
                                values={formValues}
                                onChange={handleFormChange}
                                idField={schema.cards.idColumn}
                                isNew={modal.type === 'createCard'}
                            />
                            {modal.type === 'editCard' && schema.cards.imageTypes.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Images</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {schema.cards.imageTypes.map(type => (
                                            <ImageUploadWidget key={type} imageType={type} label={type}
                                                               currentUrl={formValues[Object.values(TCG_IMAGE_COLS[selectedTcg.name]?.cards || {})[schema.cards.imageTypes.indexOf(type)]] || ''}
                                                               onUpload={handleCardImageUpload}/>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button onClick={() => setModal(null)}
                                className="px-4 py-2 border rounded-md text-sm hover:bg-gray-50">Cancel
                        </button>
                        <button onClick={handleSave} disabled={saving}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                            <Save className="w-4 h-4 mr-1"/>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// Frontend-side image column mapping (mirrors backend schema)
const TCG_IMAGE_COLS = {
    pokemon: {
        sets: {logo: 'logo_image_url', symbol: 'symbol_image_url'},
        cards: {small: 'small_image_url', large: 'large_image_url'}
    },
    pokemon_ja: {
        sets: {logo: 'logo_image_url', symbol: 'symbol_image_url'},
        cards: {small: 'small_image_url', large: 'large_image_url'}
    },
    pokemon_zh_tw: {
        sets: {logo: 'logo_image_url', symbol: 'symbol_image_url'},
        cards: {small: 'small_image_url', large: 'large_image_url'}
    },
    lorcana: {
        sets: {},
        cards: {image: 'image_url'}
    },
    magic: {
        sets: {icon: 'icon_svg_uri'},
        cards: {image: 'image_url', art_crop: 'art_crop_url'}
    }
};

// Stub for getSetThumb to resolve the reference
function getSchema_frontend(tcgName) {
    if (tcgName?.startsWith('pokemon')) return TCG_IMAGE_COLS.pokemon;
    return TCG_IMAGE_COLS[tcgName] || {sets: {imageColumns: {}}, cards: {imageColumns: {}}};
}

export default AdminTcgData;
